import React, { useContext, useEffect, useState } from "react";
import DraftsCredentialTemplateCard from "../../components/Cards/DraftsCredentialTemplateCard/DraftsCredentialTemplateCard";
import { GET, PUT } from "../../service/CRUDService";
import EntityConstants from "../../configs/EntityConstants";
import AuthContext from "../../store/auth-context";
import Toast from "../../common/Toast/Toast";
import ArrowLeftIcon from "../../resources/icons/arrow-narrow-left.svg";
import CfLoader from "../../common/CfLoader/CfLoader";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { pageRootTestIds } from "../../testing/pageRootTestIds";

const ViewAllDrafts = () => {
  const { maxLimit, currentStartDay, currentEndDay, timestamp } = useParams();

  const authContext = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [pageName, setPageName] = useState("");
  const [isLodingCredTemplates, setIsLoadingCredTemplates] = useState(false);
  const [offset, setOffset] = useState(0);
  const [prevOffset, setPrevOffset] = useState(0);
  const [limit] = useState(8);
  const [startDay, setStartDay] = useState(currentStartDay);
  const [endDay, setEndDay] = useState(currentEndDay);
  const [count, setCount] = useState(0);
  const [recentList, setRecentList] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [pervList, setPervList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearched, setIsSearched] = useState(false);
  const [hasMoreResults, setHasMoreResults] = useState(true);

  // Create mapping for URL slugs to translated labels
  const labelMap = {
    recent: t("drafts:recent_filter"),
    "last-30-days": t("drafts:last_30_days_filter"),
    "last-3-months": t("drafts:last_3_months_filter"),
    "last-6-months": t("drafts:last_6_months_filter"),
    others: t("drafts:others_filter"),
  };

  // Map URL slugs to category names for search logic
  const categoryMap = {
    recent: "Recent",
    "last-30-days": "Last 30 Days",
    "last-3-months": "Last 3 Months",
    "last-6-months": "Last 6 Months",
    others: "Others",
  };

  const SEARCH = async (e) => {
    e.preventDefault();
    const searchTerm = e.target.value.trim();

    // Use the timestamp parameter to determine category
    const category = categoryMap[timestamp] || "Recent";

    if (category === "Recent") {
      setStartDay(0);
      setEndDay(7);
    } else if (category === "Last 30 Days") {
      setStartDay(7);
      setEndDay(30);
    } else if (category === "Last 3 Months") {
      setStartDay(30);
      setEndDay(90);
    } else if (category === "Last 6 Months") {
      setStartDay(90);
      setEndDay(180);
    } else {
      setStartDay(180);
      setEndDay(365);
    }

    if (searchTerm.length > 2) {
      setSearchTerm(searchTerm);
      setIsSearching(true);
      setRecentList([]);
      setHasMoreResults(true);
      try {
        const response = await GET(
          `${EntityConstants.GET_DRAFTS_TEMPLATES}limit=${limit}&offset=${offset}&start_day=${startDay}&end_day=${endDay}&search=${searchTerm}`,
        );
        if (response.ok) {
          setPervList([]);
          const data = await response.json();
          if (data.results) {
            setRecentList(data.results);
            setCount(data.count);
            setOffset(0);
            setIsSearched(true);
          } else {
            setHasMoreResults(false);
            setRecentList([]);
          }
        }
      } catch (error) {
        setIsSearching(false);
        console.error("Error during search:", error);
        setRecentList([]);
      } finally {
        setIsSearching(false);
      }
    } else {
      setRecentList([...pervList]);
      setIsSearching(false);
      setIsSearched(false);
    }
  };

  const GET_ISSUER_DRAFTS = (limit, offset, searchTerm = "") => {
    if (!hasMoreResults) return;
    setIsLoadingCredTemplates(true);
    const url = `${EntityConstants.GET_DRAFTS_TEMPLATES}limit=${limit}&offset=${offset}&start_day=${startDay}&end_day=${endDay}&search=${searchTerm}`;

    GET(url)
      .then((response) => response.json())
      .then((data) => {
        if (data.error) {
          console.log(data.error);
        } else {
          setPrevOffset(offset - limit);
          setOffset(offset);
          setCount(data.count);
          if (data.results.length === 0) {
            setHasMoreResults(false);
          } else {
            setPervList([...pervList, ...data.results]);
          }
        }
      })
      .then(() => setIsLoadingCredTemplates(false))
      .catch((error) => {
        console.error(error);
      })
      .then(() => setIsLoadingCredTemplates(false));
  };

  const removeCredentialFromList = async (credentialId) => {
    try {
      const response = await PUT(
        `${EntityConstants.PUT_REVOKE_DRAFT}?issuer_credential_template_id=${credentialId}`,
      );
      const data = await response.json();

      if (response.ok) {
        setRecentList((prev) =>
          prev.filter(
            (item) => item.issuer_credential_template_id !== credentialId,
          ),
        );
        setPervList((prev) =>
          prev.filter(
            (item) => item.issuer_credential_template_id !== credentialId,
          ),
        );

        Toast({
          type: "success",
          title: t("drafts:success"),
          message: `${data.details}`,
        });
      } else {
        console.log(data.error);
        Toast({
          type: "error",
          title: t("drafts:error"),
          message: `${data.error}`,
        });
      }
    } catch (error) {
      Toast({
        type: "error",
        title: t("drafts:error"),
        message: t("drafts:error_deleting_draft"),
      });
    }
  };

  useEffect(() => {
    if (pervList) {
      setRecentList([...pervList]);
    }
  }, [pervList]);

  useEffect(() => {
    // Use the mapping to get the translated page name
    if (timestamp && labelMap[timestamp]) {
      setPageName(labelMap[timestamp]);
    }
  }, [timestamp, t]); // Add t to dependencies to update on language change

  useEffect(() => {
    if (
      authContext.hasDiscoveryReachedBottom &&
      recentList.length < count &&
      prevOffset !== offset + limit &&
      hasMoreResults
    ) {
      GET_ISSUER_DRAFTS(limit, offset + limit, searchTerm);
    }
  }, [authContext.hasDiscoveryReachedBottom, recentList]);

  useEffect(() => {
    if (maxLimit > 0) {
      const MAX_LIMIT = parseInt(maxLimit);
      MAX_LIMIT && GET_ISSUER_DRAFTS(limit, offset);
    }
  }, [maxLimit]);

  useEffect(() => {
    authContext.lastVisitedPathHandler(location.pathname);
  }, []);

  return (
    <div
      className="flex flex-col items-center w-full mt-20 bg-cfSecondary min-h-screen"
      id={pageRootTestIds.viewAllDrafts}
    >
      <div
        className={`w-full bg-cfSearchContainer flex justify-center py-[0.8rem] px-4`}
      >
        <div
          className={`flex flex-col sm:flex-row items-start sm:items-center justify-between w-full`}
        >
          <div className="capitalize flex items-center gap-2 text-cfHeadingText font-semibold text-lg self-center md:self-start my-auto">
            <button
              onClick={() => navigate(-1)}
              className="cursor-pointer focus:outline-none"
              aria-label="Go back"
              id="view-all-drafts-back"
            >
              <img
                src={ArrowLeftIcon}
                alt="Back arrow"
                className="cursor-pointer"
              />
            </button>
            <p>{pageName}</p>
          </div>
          {/* Search */}
          <form
            className=" bg-cfSecondary p-2 rounded-lg w-full sm:w-[25rem] self-center ml-[0.630rem] md:self-end md:m-0"
            id="view-all-drafts-search-form"
            onSubmit={(e) => e.preventDefault()}
          >
            <input
              type="text"
              name="search"
              id="view-all-drafts-input-search"
              onChange={(e) => SEARCH(e)}
              placeholder={`${t("drafts:search_in")} ${pageName}`}
              className="outline-none border-none p-0 placeholder:text-sm w-full"
              style={{ fontSize: "0.875rem", lineHeight: "1.25rem" }}
            />
          </form>
        </div>
      </div>

      <div className="w-full p-4">
        <div className="flex items-center gap-6 flex-wrap w-full">
          {recentList.map(
            ({
              issuer_credential_template_id,
              credential_template_id,
              certificate_name,
              thumbnail_image,
              created_at,
              updated_at,
              description,
              tagsList,
              thumbnail_webp_url,
              template_type,
            }) => (
              <div
                key={issuer_credential_template_id}
                id={`view-all-drafts-card-${issuer_credential_template_id}`}
              >
              <DraftsCredentialTemplateCard
                name={certificate_name}
                credential_template_id={credential_template_id}
                issuer_credential_template_id={issuer_credential_template_id}
                created_at={created_at}
                //! remove this when `updated_at` is added 'lastEdited={created_at?created_at:"14 Jan 2024"}'
                lastEdited={updated_at}
                thumbnail_image={thumbnail_image}
                navigate_url={`/issuer/certificate/edit/${credential_template_id}/${issuer_credential_template_id}/${template_type}`}
                description={description}
                tagsList={tagsList}
                removeCredentialFromList={removeCredentialFromList}
                thumbnail_webp_url={thumbnail_webp_url}
                template_type={template_type}
              />
              </div>
            ),
          )}
          {(isLodingCredTemplates || isSearching) && (
            <div className="w-full flex justify-center items-center">
              <CfLoader />
            </div>
          )}
          {isSearched && !isSearching && recentList.length === 0 && (
            <p
              className="w-full flex justify-center text-grey-50 font-semibold text-lg mt-[1rem]"
              id="view-all-drafts-no-results"
            >
              {t("drafts:no_results_found")}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewAllDrafts;
